import React, { useEffect, useRef, useState } from "react";
import MaterialTable from "material-table";
import { Box, Button, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Radio, RadioGroup, Select, Typography } from "@material-ui/core";
import { useSnackbar } from "notistack";
import { postImport, ImportUserData } from "api/admin/import";
import { GridTextField } from "components";
import Papa from "papaparse";
import tableI18n from "components/DefaultTable/i18n";
import i18n from "./i18n";

// prop types
interface ImportTabProps {
    clubId?: string,
}

// name changes table
const ImportTab = ({ clubId }: ImportTabProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    const displayCustomError = (error: any) => enqueueSnackbar(
        `${i18n.insufficientNumberOfSeatsInCompanyForImportError(error.associated)}`,
        { variant: "error" },
    );

    const VALID_SITE_LENGTH = 50;

    // ui state
    const [email, setEmail] = useState("");
    const [firstName, setFirstName] = useState("");
    const [isLoading, setLoading] = useState(false);
    const [isSingleImport, setSingleImport] = useState(true);
    const [lastName, setLastName] = useState("");
    const [site, setSite] = useState("");
    const [parseResults, setParseResults] = useState<Papa.ParseResult<any>>();
    const csvFileInput = useRef<HTMLInputElement>(null);

    // parse csv file
    const parseCsvFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files![0];
        if (file) {
            Papa.parse(file, {
                complete: setParseResults,
                error: (error) => displayError({ description: error.message, type: error.type }),
                header: true,
                skipEmptyLines: true,
            });
        }
    };

    // clear fields
    useEffect(() => { setFirstName(""); setLastName(""); setEmail(""); setSite(""); }, [isSingleImport]);

    // execute import
    const executeImport = async () => {
        setLoading(true);
        try {
            let users: ImportUserData[] = [];
            if (isSingleImport) {
                users = [{ firstName, lastName, email, site }];
            } else {
                users = parseResults?.data.map((d) => ({
                    email: d[email],
                    firstName: d[firstName],
                    lastName: d[lastName],
                    site: d[site],
                })) || [];
            }
            await postImport({ clubId: clubId!, users });
            enqueueSnackbar(i18n.importSuccess(users.length), { variant: "success" });
            setEmail("");
            setFirstName("");
            setLastName("");
            setSite("");
            setParseResults(undefined);
        } catch (error: any) {
            if (error.type === "insufficientNumberOfSeatsInCompanyForImport") {
                displayCustomError(error);
            } else {
                displayError(error);
            }
        }
        setLoading(false);
    };

    // render
    return (
        <Box style={{ alignItems: "center", display: "flex", flexDirection: "column", padding: "16px" }}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Typography variant="h6">
                        {i18n.import}
                    </Typography>

                    {/* single/multiple import? */}
                    <FormControl margin="dense">
                        <RadioGroup
                            row
                            value={isSingleImport ? "single" : "csv"}
                            onChange={(e) => setSingleImport(e.target.value === "single")}
                        >
                            <FormControlLabel
                                control={<Radio color="primary" />}
                                label={i18n.singleImport}
                                value="single"
                            />
                            <FormControlLabel
                                control={<Radio color="primary" />}
                                label={i18n.csvImport}
                                value="csv"
                            />
                        </RadioGroup>
                    </FormControl>
                </Grid>

                {/* single import */}
                {isSingleImport && (
                    <>
                        {/* fields */}
                        <GridTextField label={i18n.firstName} value={firstName} onChange={setFirstName} size={7} />
                        <GridTextField label={i18n.lastName} value={lastName} onChange={setLastName} size={7} />
                        <GridTextField label={i18n.emailAddress} value={email} onChange={setEmail} size={7} type="email" />
                        <GridTextField
                            error={Number(site.length || 0) > VALID_SITE_LENGTH}
                            helperText={`${Number(site.length || 0)} / ${VALID_SITE_LENGTH}`}
                            maxLength={VALID_SITE_LENGTH}
                            label={i18n.site}
                            value={site}
                            onChange={setSite}
                            size={7}
                        />

                        {/* import button */}
                        <Grid item xs={7} style={{ alignItems: "center", display: "flex", justifyContent: "center" }}>
                            <Button
                                color="primary"
                                disabled={isLoading || !(email && firstName && lastName)}
                                variant="contained"
                                onClick={executeImport}
                            >
                                {i18n.import}
                            </Button>
                        </Grid>
                    </>
                )}

                {/* csv import */}
                {!isSingleImport && (
                    <>
                        {/* left */}
                        <Grid item xs={5}>
                            <Grid container spacing={2}>
                                {/* desc */}
                                <Grid item xs={12}>
                                    <Typography variant="body2">{i18n.importDesc}</Typography>
                                </Grid>

                                {/* file input */}
                                <Grid item xs={12} style={{ alignItems: "center", display: "flex", justifyContent: "center" }}>
                                    <Button color="primary" variant="contained" onClick={() => csvFileInput.current!.click()}>
                                        {i18n.browseFile}
                                    </Button>
                                    <input accept="text/csv" ref={csvFileInput} style={{ display: "none" }} type="file" onChange={parseCsvFile} />
                                </Grid>

                                {/* column selectors */}
                                <Grid item xs={12}>
                                    <FormControl fullWidth variant="outlined">
                                        <InputLabel>{i18n.firstName}</InputLabel>
                                        <Select
                                            label={i18n.firstName}
                                            value={firstName}
                                            onChange={(e) => setFirstName(e.target.value as string)}
                                        >
                                            <MenuItem disabled>{i18n.selectColumn}</MenuItem>
                                            {parseResults?.meta.fields?.map((f) => <MenuItem value={f} key={f}>{f}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth variant="outlined">
                                        <InputLabel>{i18n.lastName}</InputLabel>
                                        <Select
                                            label={i18n.lastName}
                                            value={lastName}
                                            onChange={(e) => setLastName(e.target.value as string)}
                                        >
                                            <MenuItem disabled>{i18n.selectColumn}</MenuItem>
                                            {parseResults?.meta.fields?.map((f) => <MenuItem value={f} key={f}>{f}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth variant="outlined">
                                        <InputLabel>{i18n.emailAddress}</InputLabel>
                                        <Select
                                            label={i18n.emailAddress}
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value as string)}
                                        >
                                            <MenuItem disabled>{i18n.selectColumn}</MenuItem>
                                            {parseResults?.meta.fields?.map((f) => <MenuItem value={f} key={f}>{f}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth variant="outlined">
                                        <InputLabel>{i18n.site}</InputLabel>
                                        <Select
                                            label={i18n.site}
                                            value={site}
                                            onChange={(e) => setSite(e.target.value as string)}
                                        >
                                            <MenuItem disabled>{i18n.selectColumn}</MenuItem>
                                            {parseResults?.meta.fields?.map((f) => <MenuItem value={f} key={f}>{f}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* import button */}
                                <Grid item xs={12} style={{ alignItems: "center", display: "flex", justifyContent: "center" }}>
                                    <Button
                                        color="primary"
                                        disabled={isLoading || !(email && firstName && lastName)}
                                        variant="contained"
                                        onClick={executeImport}
                                    >
                                        {i18n.import}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* right, preview */}
                        <Grid item xs={7}>
                            <MaterialTable
                                columns={[
                                    { field: firstName, title: i18n.firstName },
                                    { field: lastName, title: i18n.lastName },
                                    { field: email, title: i18n.emailAddress },
                                    { field: site, title: i18n.site },
                                ]}
                                data={parseResults?.data || []}
                                localization={tableI18n.localization}
                                options={{
                                    draggable: false,
                                    emptyRowsWhenPaging: false,
                                    pageSizeOptions: [25],
                                    pageSize: 25,
                                    toolbar: false,
                                }}
                            />
                        </Grid>
                    </>
                )}
            </Grid>
        </Box>
    );
};

export default ImportTab;
